import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useBibleSearch } from "@/hooks/use-bible-search";
import {
  createScriptureReference,
  listBibleVersions,
  BibleVersionOption,
  DEFAULT_TRANSLATION,
} from "@/lib/bible-api";
import { Loader2, SearchIcon, CornerDownLeft, BookmarkPlus } from "lucide-react";

interface BibleVersePickerProps {
  documentId?: number;
  onVerseSelect?: () => void;
  onInsert?: (payload: { reference: string; text: string }) => void;
}

export function BibleVersePicker({
  onVerseSelect,
  onInsert,
}: BibleVersePickerProps) {
  const { toast } = useToast();
  const { result, isLoading, error, searchReference, clear } = useBibleSearch();
  const [reference, setReference] = useState("");
  const [versions, setVersions] = useState<BibleVersionOption[]>([
    { id: 111, abbreviation: "NIV", title: "New International Version" },
  ]);
  const [bibleId, setBibleId] = useState(111);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    listBibleVersions()
      .then((data) => {
        if (data.versions?.length) {
          setVersions(data.versions);
          const preferred =
            data.versions.find((v) => v.abbreviation === (data.default || DEFAULT_TRANSLATION)) ||
            data.versions.find((v) => v.abbreviation === DEFAULT_TRANSLATION) ||
            data.versions[0];
          if (preferred) setBibleId(preferred.id);
        }
      })
      .catch(() => {
        /* keep NIV fallback */
      });
  }, []);

  useEffect(() => {
    setSaved(false);
  }, [result]);

  const selected =
    versions.find((v) => v.id === bibleId) || versions[0] || {
      id: 111,
      abbreviation: DEFAULT_TRANSLATION,
      title: "New International Version",
    };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reference.trim()) {
      toast({
        title: "Enter a reference",
        description: 'Try something like "John 3:16" or "Psalm 23:1-3".',
        variant: "destructive",
      });
      return;
    }
    clear();
    await searchReference(reference.trim(), selected.abbreviation, selected.id);
  };

  const saveReference = async () => {
    if (!result) return;
    try {
      await createScriptureReference({
        book: result.book || result.reference.split(" ")[0],
        chapter: result.chapter || 1,
        verse_start: result.verse_start || 1,
        verse_end: result.verse_end || result.verse_start || 1,
        translation: result.translation || selected.abbreviation,
        text: result.text,
      });
      setSaved(true);
      toast({
        title: "Saved",
        description: `${result.reference} (${result.translation}) saved to your library.`,
      });
      onVerseSelect?.();
    } catch (err) {
      console.error(err);
      toast({
        title: "Could not save",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="bible-ref">Scripture reference</Label>
          <Input
            id="bible-ref"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="John 3:16 · Psalm 23:1-3 · 1 John 4:8"
            autoFocus
          />
        </div>

        <div className="space-y-1.5">
          <Label>Translation</Label>
          <Select
            value={String(bibleId)}
            onValueChange={(v) => setBibleId(Number(v))}
          >
            <SelectTrigger>
              <SelectValue placeholder="NIV" />
            </SelectTrigger>
            <SelectContent>
              {versions.map((v) => (
                <SelectItem key={v.id} value={String(v.id)}>
                  {v.abbreviation} — {v.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Powered by YouVersion. Default is NIV.
          </p>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Looking up…
            </>
          ) : (
            <>
              <SearchIcon className="mr-2 h-4 w-4" />
              Look up passage
            </>
          )}
        </Button>
      </form>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {result && (
        <Card>
          <CardContent className="pt-5 space-y-4">
            <div>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <h4 className="font-medium">{result.reference}</h4>
                <span className="text-xs text-muted-foreground shrink-0">
                  {result.translation}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-foreground/90">
                {result.text}
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {onInsert && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() =>
                    onInsert({
                      reference: `${result.reference} (${result.translation})`,
                      text: result.text,
                    })
                  }
                >
                  <CornerDownLeft className="h-3.5 w-3.5 mr-1.5" />
                  Insert at cursor
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant={saved ? "outline" : "secondary"}
                disabled={saved}
                onClick={saveReference}
              >
                <BookmarkPlus className="h-3.5 w-3.5 mr-1.5" />
                {saved ? "Saved" : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
